import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, PanelLeftOpen } from 'lucide-react';
import { FunnelSimpleIcon, ListIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import type { MapSearchArea } from '../../shared/results_page/map_search_area.js';
import type { HotelMapView } from '../models.js';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { HOTEL_NARROW_QUERY } from '../search/hotel_narrow.js';
import { HotelSearchBar, type HotelSearchBarProps } from '../search/hotel_search_bar.js';
import { HotelMap } from './hotel_map.js';
import {
  HotelResultsFilters,
  matchesHotelResultsFilters,
  type HotelFilterableOffer,
  type HotelResultsFiltersCopy,
  type HotelResultsFilterValue,
} from './hotel_results_filters.js';
import { HotelResultsFiltersSheet } from './hotel_results_filters_sheet.js';
import type { HotelResultsCopy } from './hotel_results.js';
import './hotel_map_fullscreen.css';

export type HotelMapFullscreenCopy = {
  backLabel: string;
  /** O botão flutuante do palco ESTREITO que volta à lista ("Lista", #2665) —
   *  o par do `showMapLabel` da página de resultados. */
  showListLabel: string;
  expandFiltersLabel: string;
  visibleHotelsLabel(count: number): string;
  loadMoreLabel: string;
  loadingMoreLabel: string;
  allLoadedLabel: string;
  /** "Buscar nesta área" — estado ocioso do controle que re-busca o viewport. */
  searchAreaLabel: string;
  /** "Buscando…" — o mesmo controle enquanto a busca da área está no ar. */
  searchingAreaLabel: string;
};

export type HotelMapFullscreenProps = {
  map: HotelMapView;
  offers: readonly HotelFilterableOffer[];
  currency: string;
  resultsCopy: HotelResultsCopy;
  copy: HotelMapFullscreenCopy;
  filtersCopy: HotelResultsFiltersCopy;
  search: HotelSearchBarProps;
  /** Filtros CONTROLADOS: o dono é o chamador — a MESMA fonte da lista cheia,
   *  para o corte viajar lista↔mapa e sobreviver ao fechamento desta tela. */
  filters: HotelResultsFilterValue;
  onFiltersChange(filters: HotelResultsFilterValue): void;
  selectedHotelId?: string;
  locked?: boolean;
  /** Ainda há página depois desta — o MESMO `hasMore` da lista: o conjunto é um
   *  só, e o que o botão flutuante carregar aparece também lá. */
  hasMore?: boolean;
  /** Uma página está a caminho: o controle segura (disabled) em vez de disparar
   *  outra chamada por cima da que está em voo. */
  loadingMore?: boolean;
  /** Optional: sem quem carregar (host sem paginação) o controle não é montado. */
  onLoadMore?: () => void;
  /** A identidade do CONJUNTO paginado (sessão + busca + corte). Quando muda —
   *  busca nova feita desta própria tela, corte novo — o aviso de "todos
   *  carregados" é descartado: ele responde ao gesto DESTE conjunto, e um
   *  conjunto novo que já nasce sem mais páginas não foi pedido por ninguém. */
  loadMoreResetToken?: string;
  /** Ver `HotelResultsFilters.countsPartial` — o dono da lista sabe se o
   *  fornecedor tem mais hotéis do que os carregados. */
  countsPartial?: boolean;
  /** Liga o "buscar nesta área" no mapa cheio (ver `HotelMap.onSearchArea`).
   *  Ausente ⇒ o botão nunca arma — o kit não inventa busca. */
  onSearchArea?: (area: MapSearchArea) => void | Promise<void>;
  /** Coleta inicial em andamento: enquanto TRUE o gesto não arma o botão. */
  searchAreaPending?: boolean;
  onClose(): void;
  onOpenDetails(hotelId: string): void;
  /** Optional: without a command sink the reply composer is not rendered
   *  (deterministic hosts have no conversation to reply to). */
  onCommandSubmit?(command: string): void;
};

/**
 * O "carregar mais" do mapa cheio — botão flutuante, e DELIBERADAMENTE não
 * auto-load no `idle` da câmera: página nova re-enquadra os pinos
 * (`useMapboxFrame` refaz o `fitBounds` a cada assinatura de pontos), o
 * enquadramento dispara outro `idle`, e o "carregar ao navegar" viraria um laço
 * que engole todas as páginas sozinho. A paginação também não é geográfica
 * (página N da MESMA consulta, não "buscar nesta área"), então o gesto honesto é
 * o pedido explícito — o mesmo caminho acessível que a `LoadMoreSentinel` da
 * lista já garante por botão.
 *
 * Depois que o viajante pediu mais e as páginas acabaram, o aviso de "todos
 * carregados" responde ao gesto; sem pedido nenhum, mapa limpo.
 */
function HotelMapLoadMore({
  hasMore,
  loadingMore,
  copy,
  resetToken,
  onLoadMore,
}: {
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly copy: HotelMapFullscreenCopy;
  readonly resetToken?: string;
  readonly onLoadMore?: () => void;
}): ReactNode {
  const [requested, setRequested] = useState(false);
  // Reset no RENDER (o mesmo padrão do dono da lista): conjunto novo em cena
  // com o aviso velho ainda pintado seria exatamente o vazamento a evitar.
  const [seenToken, setSeenToken] = useState(resetToken);
  if (seenToken !== resetToken) {
    setSeenToken(resetToken);
    setRequested(false);
  }
  if (!onLoadMore) return null;
  if (hasMore || loadingMore) {
    return (
      <div className="hui-hotel-map-fullscreen-load-more">
        <Button
          type="button"
          variant="outline"
          className="rounded-full shadow-md"
          disabled={loadingMore}
          onClick={() => {
            if (loadingMore) return;
            setRequested(true);
            onLoadMore();
          }}
        >
          {loadingMore ? copy.loadingMoreLabel : copy.loadMoreLabel}
        </Button>
      </div>
    );
  }
  if (!requested) return null;
  return (
    <output className="hui-hotel-map-fullscreen-load-more hui-hotel-map-fullscreen-all-loaded">
      {copy.allLoadedLabel}
    </output>
  );
}

/**
 * Se os filtros estão em cena. A coluna nasce ABERTA no desktop (há espaço ao
 * lado do mapa) e FECHADA no celular (#2664), onde ela é a folha de tela cheia
 * que só abre por gesto. Trocar de palco no meio (girar o aparelho, estreitar a
 * janela com a coluna aberta) volta ao padrão do palco novo — senão a coluna
 * aberta do desktop virava a folha do celular abrindo sozinha, sem toque
 * nenhum (achado do CodeRabbit, PR #2711). Ressincroniza no render, como o
 * `resetToken` do carregar-mais: é derivação de estado, não efeito.
 */
/** Quanto a coluna de filtros do desktop toma da esquerda do mapa: 0.75rem de
 *  margem + 18.5rem de coluna + 0.75rem de respiro até o primeiro pino, a 16px
 *  (`.hui-hotel-map-fullscreen > .iuk-results-filters` no CSS ao lado). */
const FULLSCREEN_FILTERS_COLUMN_PX = 320;

function useFiltersOpen(narrow: boolean): [boolean, (open: boolean) => void] {
  const [filtersOpen, setFiltersOpen] = useState(!narrow);
  const [seenNarrow, setSeenNarrow] = useState(narrow);
  if (seenNarrow !== narrow) {
    setSeenNarrow(narrow);
    setFiltersOpen(!narrow);
  }
  return [filtersOpen, setFiltersOpen];
}

/** A alternância Mapa→Lista do palco ESTREITO (#2665): o par do `Mapa` da
 *  página de resultados, flutuando no pé — além do voltar do canto. */
function ShowListFab({
  label,
  onClose,
}: {
  readonly label: string;
  readonly onClose: () => void;
}): ReactNode {
  return (
    <div className="hui-hotel-map-fullscreen-show-list">
      <Button type="button" size="lg" className="rounded-full shadow-lg" onClick={onClose}>
        <ListIcon data-icon="inline-start" aria-hidden="true" />
        {label}
      </Button>
    </div>
  );
}

/**
 * Os filtros do mapa cheio no palco ESTREITO (#2664): a tela abre com o mapa
 * limpo e um gatilho só, no canto superior direito (acima dos controles do
 * mapa), e o toque abre a MESMA folha de tela cheia da página de resultados
 * (`HotelResultsFiltersSheet`, #2663) — rascunho, X e `Aplicar filtros`. A
 * coluna flutuante e a folha parcial de 72dvh saem de cena: no celular elas
 * cobriam metade do mapa já na abertura.
 */
function NarrowMapFilters({
  open,
  onOpenChange,
  offers,
  currency,
  filters,
  filtersCopy,
  locked,
  countsPartial,
  onFiltersChange,
  onCommandSubmit,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly offers: readonly HotelFilterableOffer[];
  readonly currency: string;
  readonly filters: HotelResultsFilterValue;
  readonly filtersCopy: HotelResultsFiltersCopy;
  readonly locked: boolean;
  readonly countsPartial: boolean;
  readonly onFiltersChange: (filters: HotelResultsFilterValue) => void;
  readonly onCommandSubmit: ((command: string) => void) | undefined;
}): ReactNode {
  const triggerRef = useRef<HTMLButtonElement>(null);
  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="icon"
        className="hui-hotel-map-fullscreen-expand-filters shadow-md"
        aria-label={filtersCopy.filtersLabel}
        onClick={() => {
          onOpenChange(true);
        }}
      >
        <FunnelSimpleIcon aria-hidden="true" />
      </Button>
      <HotelResultsFiltersSheet
        open={open}
        onOpenChange={onOpenChange}
        returnFocusTo={triggerRef}
        offers={offers}
        currency={currency}
        value={filters}
        copy={filtersCopy}
        locked={locked}
        countsPartial={countsPartial}
        onApply={onFiltersChange}
        {...(onCommandSubmit ? { onCommandSubmit } : {})}
      />
    </>
  );
}

export function HotelMapFullscreen({
  map,
  offers,
  currency,
  resultsCopy,
  copy,
  filtersCopy,
  search,
  filters,
  onFiltersChange,
  selectedHotelId,
  locked = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  loadMoreResetToken,
  countsPartial = false,
  onSearchArea,
  searchAreaPending = false,
  onClose,
  onOpenDetails,
  onCommandSubmit,
}: Readonly<HotelMapFullscreenProps>): ReactNode {
  /* O palco ESTREITO (#2665): lista e mapa são duas telas, e a volta à lista
     ganha o botão flutuante do pé — o par do `Mapa` da página de resultados.
     A MESMA régua da barra e da página (`HOTEL_NARROW_QUERY`). */
  const narrow = useMediaQuery(HOTEL_NARROW_QUERY);
  const [filtersOpen, setFiltersOpen] = useFiltersOpen(narrow);
  const collapseFiltersRef = useRef<HTMLButtonElement>(null);
  const expandFiltersRef = useRef<HTMLButtonElement>(null);
  /* Where the focus lands after the toggle: a new object per toggle, so the
     effect below runs once per press (a viewport change of `filtersOpen` does not). */
  const [focusAfterToggle, setFocusAfterToggle] = useState<{
    readonly target: 'collapse' | 'expand';
  } | null>(null);
  const filteredOffers = useMemo(
    () => offers.filter((facts) => matchesHotelResultsFilters(facts, filters)),
    [filters, offers],
  );
  /* Memoizado, e não `filteredOffers.map(...)` no JSX: array novo a cada render
     remonta todos os marcadores, e o pino sob o cursor perde o hover no meio do
     gesto — abrir ou fechar a coluna de filtros bastava. */
  const mapOffers = useMemo(() => filteredOffers.map(({ offer }) => offer), [filteredOffers]);
  useEffect(() => {
    if (!focusAfterToggle) return;
    const { target } = focusAfterToggle;
    queueMicrotask(() => {
      (target === 'collapse' ? collapseFiltersRef : expandFiltersRef).current?.focus();
    });
  }, [focusAfterToggle]);

  return (
    <div className="genui2 hui-hotel-map-fullscreen">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="hui-hotel-map-fullscreen-back shadow-md"
        aria-label={copy.backLabel}
        onClick={onClose}
      >
        <ArrowLeft aria-hidden="true" />
      </Button>
      <div className="hui-hotel-map-fullscreen-search">
        <HotelSearchBar {...search} readOnly={locked || search.readOnly} />
      </div>
      <output className="sr-only" aria-live="polite">
        {copy.visibleHotelsLabel(filteredOffers.length)}
      </output>
      <div className="hui-hotel-map-fullscreen-map">
        {/* A barra de busca por cima, e ela desce mais que a do palco: 16 de
            topo + 48 de pílula + a calha de 12. Aqui não há dock de cards; o
            que cobre a ponta OESTE é a coluna de filtros do desktop, e ela
            entra na folga enquanto está aberta — sem isso os pinos da esquerda
            paravam atrás dela (#3217 C5). */}
        <HotelMap
          map={map}
          offers={mapOffers}
          overlays={{
            top: 76,
            ...(filtersOpen && !narrow ? { left: FULLSCREEN_FILTERS_COLUMN_PX } : {}),
          }}
          selectLabel={resultsCopy.selectLabel}
          photoCopy={{
            previousPhotoLabel: resultsCopy.previousPhotoLabel,
            nextPhotoLabel: resultsCopy.nextPhotoLabel,
          }}
          detailsLabel={resultsCopy.detailsLabel}
          selectedHotelId={selectedHotelId}
          selectionLocked={locked}
          controls={{
            zoomInLabel: resultsCopy.mapZoomInLabel,
            zoomOutLabel: resultsCopy.mapZoomOutLabel,
            recenterLabel: resultsCopy.mapRecenterLabel,
          }}
          searchAreaCopy={{
            searchLabel: copy.searchAreaLabel,
            searchingLabel: copy.searchingAreaLabel,
          }}
          searchAreaPending={searchAreaPending}
          {...(onSearchArea ? { onSearchArea } : {})}
          onHotelSelect={onOpenDetails}
          onDetailsOpen={onOpenDetails}
        />
      </div>
      <HotelMapLoadMore
        hasMore={hasMore}
        loadingMore={loadingMore}
        copy={copy}
        resetToken={loadMoreResetToken}
        {...(onLoadMore ? { onLoadMore } : {})}
      />
      {narrow && <ShowListFab label={copy.showListLabel} onClose={onClose} />}
      {narrow ? (
        <NarrowMapFilters
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          offers={offers}
          currency={currency}
          filters={filters}
          filtersCopy={filtersCopy}
          locked={locked}
          countsPartial={countsPartial}
          onFiltersChange={onFiltersChange}
          onCommandSubmit={onCommandSubmit}
        />
      ) : filtersOpen ? (
        <HotelResultsFilters
          offers={offers}
          currency={currency}
          value={filters}
          copy={filtersCopy}
          locked={locked}
          countsPartial={countsPartial}
          collapseButtonRef={collapseFiltersRef}
          onChange={onFiltersChange}
          onCollapse={() => {
            setFocusAfterToggle({ target: 'expand' });
            setFiltersOpen(false);
          }}
          {...(onCommandSubmit ? { onCommandSubmit } : {})}
        />
      ) : (
        <Button
          ref={expandFiltersRef}
          type="button"
          variant="outline"
          size="icon"
          className="hui-hotel-map-fullscreen-expand-filters shadow-md"
          aria-label={copy.expandFiltersLabel}
          onClick={() => {
            setFocusAfterToggle({ target: 'collapse' });
            setFiltersOpen(true);
          }}
        >
          <PanelLeftOpen aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}
